import { motion } from 'motion/react';
import type { PropsWithChildren } from 'react';
import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useViewportSize } from '../../hooks/useViewportSize';
import { spacing } from '../../styles/spacing.css';
import { Flex } from '../Flex/Flex';
import type { LayoutProps } from '../Layout/Layout';
import { Layout } from '../Layout/Layout';
import { SELECT_ITEM_HEIGHT, selectItem } from '../Select/Item.css';
import { AutocompleteActionOption } from './AutocompleteActionOption';
import * as styles from './AutocompleteOptions.css';
import type { AdditionalActionProps } from './hooks/useAutocomplete';

type AutocompleteContainerProps = LayoutProps<typeof motion.div> & {
  readonly additionalActionProps?: AdditionalActionProps;
  readonly maxVisibleContentItems: number;
  readonly numberOfItemsToDisplay: number;
};

export const AutocompleteContainer = forwardRef<HTMLDivElement, PropsWithChildren<AutocompleteContainerProps>>(
  ({ children, additionalActionProps, maxVisibleContentItems, numberOfItemsToDisplay, ...props }, ref) => {
    const { isMobileViewport } = useViewportSize();
    const [hasScroll, setHasScroll] = useState(numberOfItemsToDisplay < maxVisibleContentItems);

    const hasAdditionalAction = additionalActionProps?.label !== undefined;

    useEffect(
      () => setHasScroll(numberOfItemsToDisplay < maxVisibleContentItems),
      [numberOfItemsToDisplay, maxVisibleContentItems],
    );

    const refCallback = useCallback(
      (e: HTMLDivElement | null) => {
        if (typeof ref === 'function') {
          ref(e);
        }

        if (!isMobileViewport) return;

        const viewPortHeight = window.visualViewport?.height ?? 0;
        const containerTopPosition = e?.getBoundingClientRect().top ?? 0;
        e?.style.setProperty('min-height', `${Math.round(viewPortHeight - containerTopPosition)}px`);

        const progressHandler = (): void => {
          /* istanbul ignore else -- @preserve - else will never happen */
          if (e) {
            setHasScroll(e.scrollTop + e.clientHeight >= e.scrollHeight);
          }
        };

        e?.addEventListener('scroll', progressHandler);
        /* istanbul ignore next -- @preserve - can't test visualViewport?.addEventListener */
        const onResize = (): void => {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- onResize is only called when visualViewport is available
          const resizedViewportHeight = Math.floor(window.visualViewport!.height);
          e?.style.setProperty('min-height', 'auto');
          e?.style.setProperty('height', `${Math.round(resizedViewportHeight - e.getBoundingClientRect().top)}px`);
        };

        window.visualViewport?.addEventListener('resize', onResize);

        /* istanbul ignore next -- @preserve - can't be tested */
        return () => {
          e?.removeEventListener('scroll', progressHandler);
          window.visualViewport?.removeEventListener('resize', onResize);
        };
      },
      [ref, isMobileViewport],
    );

    return (
      <Layout
        animate={{ opacity: 1 }}
        as={motion.div}
        initial={isMobileViewport ? { opacity: 0 } : undefined}
        ref={refCallback}
        transition={{ duration: 0.3 }}
        {...props}
        overflow="auto"
      >
        {children}
        {hasAdditionalAction && (
          <Flex bottom="0" flexDirection="column" position="sticky">
            <Layout
              as={motion.div}
              bottom={SELECT_ITEM_HEIGHT}
              className={styles.additionalActionLinearBackground}
              height={spacing.s48}
              position="absolute"
              style={{
                [styles.backgroundOpacityVarName]: hasScroll ? 0 : 1,
              }}
              width="100%"
            />
            <Layout className={selectItem({ secondaryLabel: false })}>
              <AutocompleteActionOption label={additionalActionProps.label} onClick={additionalActionProps.onClick} />
              <Layout
                backgroundColor="$dropdownBackground"
                bottom={`-${styles.LIST_CONTAINER_PADDING}`}
                height={styles.LIST_CONTAINER_PADDING}
                left="0"
                position={{ sm: 'sticky', md: 'absolute' }}
                width="100%"
              />
            </Layout>
          </Flex>
        )}
      </Layout>
    );
  },
);
